import React, {createRef, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {IOrderProductFileModel} from '@src/@interfaces/models/Order/IOrderProductFileModel';
import FileListTableRow from './FileListTableRow';
import {AsyncFileUploaderContext} from '@src/components/AsyncFileUploader/AsyncFileUploaderContext';
import {removeUploadingFile} from '@src/_redux/order-product-files/actions';
import {debounce} from '@src/helpers/debounce-throttle';
import app from '@src/helpers/app';
import {notNull} from '@src/helpers/ts/notNull';

export default function FilesListAsTable(props: {
    uploadedFiles: IOrderProductFileModel[],
    uploadingFilesHashes: string[],
    newFiles: File[],
    onOpen: (item: IOrderProductFileModel, index: number) => Promise<void>,
    onDelete: (item: IOrderProductFileModel | File, index: number) => Promise<boolean>,
    uploaderContext?: Record<string, any>,
    disabled?: boolean,
}) {
    const {getItem} = useContext(AsyncFileUploaderContext);
    const dispatch = useDispatch();
    const {onOpen: baseOnOpen, onDelete: baseOnDelete, disabled: isDisabled} = props;

    const ref = createRef<HTMLTableElement>();
    const [maxTitleWidth, setMaxTitleWidth] = useState<number | null>(null);
    const itemsNumber = (
            props.newFiles.length +
            props.uploadingFilesHashes.length +
            props.uploadedFiles.length
    );

    useEffect(() => {
        const listener = debounce(() => {
            if (!ref.current) {
                return;
            }

            const cell = ref.current
                    .querySelector<HTMLTableColElement>('.order__files-list__row__title-cell');
            const styles = cell ? window.getComputedStyle(cell) : undefined;

            const was = ref.current.getAttribute('style');
            ref.current.removeAttribute('style');

            const maxWidth = (cell?.offsetWidth ?? 0) - (
                    +(styles?.paddingLeft ?? '0').replace(/[^\d.,]+?/guim, '') +
                    +(styles?.paddingRight ?? '0').replace(/[^\d.,]+?/guim, '')
            );

            if (was) {
                ref.current.setAttribute('style', was);
            }

            if (maxWidth) {
                setMaxTitleWidth(maxWidth);
            }
        }, 100);
        listener();

        window.addEventListener('resize', listener);

        return () => window.removeEventListener('resize', listener);
    }, [ref, itemsNumber]);

    const [onDelete, onOpen, onAbort, tableStyle] = useMemo(() => {
        const onDelete = (item: IOrderProductFileModel | File, index: number) => (): Promise<boolean> => {
            if (isDisabled) {
                return Promise.reject();
            }

            return baseOnDelete(item, index);
        };

        const onOpen = (item: IOrderProductFileModel, index: number) => (): Promise<void> => {
            return baseOnOpen(item, index);
        };

        const onAbort = (hash: string) => () => {
            setTimeout(() => {
                dispatch(removeUploadingFile(props.uploaderContext?.productCode, [hash]));
            }, 20);

            return Promise.resolve(true);
        };

        const tableStyle = maxTitleWidth ? {
            '--order__files-list-title-max-width': `${maxTitleWidth}px`
        } as React.CSSProperties : undefined;

        return [onDelete, onOpen, onAbort, tableStyle];
    }, [isDisabled, maxTitleWidth, props.uploaderContext?.productCode, baseOnDelete, baseOnOpen, dispatch]);

    const onPreview = useCallback(async (index: number) => {
        const list = [
            ...props.newFiles,
            ...props.uploadingFilesHashes.map(hash => getItem(hash)?.getFile()).filter(notNull),
            ...props.uploadedFiles.map(item => item.file),
        ];

        app('files-viewer').open(list, {Carousel: {initialSlide: index}});
    }, [props.newFiles, props.uploadingFilesHashes, props.uploadedFiles, getItem]);

    const viewer = app('files-viewer');
    let indexThrowLists = 0;

    return <table className="order__files-list" ref={ref} style={tableStyle}>
        <tbody>
            {props.newFiles.map((file, i) => {
                return <FileListTableRow
                        key={`${file.name}-${i}`}
                        index={indexThrowLists++}
                        title={file.name}
                        size={file.size}
                        isNewFile={true}
                        onPreview={onPreview}
                        onDelete={onDelete(file, i)}
                        uploaderContext={props.uploaderContext}
                />;
            })}

            {props.uploadingFilesHashes.map((hash, i) => {
                const file = getItem(hash)?.getFile();
                if (!file) {
                    return null;
                }

                return <FileListTableRow
                        key={`${hash}-${i}`}
                        index={indexThrowLists++}
                        title={file.name}
                        size={file.size}
                        isNewFile={true}
                        onPreview={onPreview}
                        onDelete={onAbort(hash)}
                        uploaderContext={props.uploaderContext}
                />;
            })}

            {props.uploadedFiles.map((item, i) => {
                const allowedToPreview = viewer.isAllowedItem(item.file);

                return <FileListTableRow
                        key={`${item.id}-${i}`}
                        index={allowedToPreview ? indexThrowLists++ : -1}
                        id={item.id}
                        title={item.file.public_name ?? item.file.file_name}
                        size={item.file.file_size ?? 0}
                        isNewFile={false}
                        onPreview={allowedToPreview ? onPreview : undefined}
                        onOpen={onOpen(item, i)}
                        onDelete={onDelete(item, i)}
                        disabled={isDisabled}
                />;
            })}
        </tbody>
    </table>;
}
